import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { Card, Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect } from "../engagement/FormControls";
import { Plus, Search, Pencil, Trash2, BookOpen, X } from "lucide-react";

const ROLES = [
  "PCP (Primary Care Provider)", "Psychiatrist", "Psychiatric Nurse Practitioner",
  "Therapist (Licensed)", "Case Manager", "Peer Support Specialist",
  "Pharmacist", "Dentist", "Ophthalmologist", "Neurologist", "Endocrinologist",
  "Cardiologist", "OB-GYN", "Physical Therapist", "Occupational Therapist",
  "Speech Therapist", "Nutritionist", "Social Worker", "Care Coordinator",
];

const EMPTY = { role: "", name: "", organization: "", phone: "", email: "",
                fax: "", address: "", npi: "", is_primary: false, notes: "",
                directory_id: "" };

export default function CareTeam({ client }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [panel, setPanel] = useState(false);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [dirSearch, setDirSearch] = useState("");
  const [dirRoleFilter, setDirRoleFilter] = useState("");
  const [dirResults, setDirResults] = useState([]);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirOpen, setDirOpen] = useState(false);
  const [addToDirOpen, setAddToDirOpen] = useState(false);
  const [dirNewForm, setDirNewForm] = useState({
    name: "", role: "", organization: "", specialty: "", phone: "", email: "",
    fax: "", npi: "", address: "", in_network: false, accepting_new_patients: true, notes: "",
  });
  const [dirNewBusy, setDirNewBusy] = useState(false);

  const load = () => api.get(`/clients/${client.id}/care-team`).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (client?.id) load(); }, [client?.id]); // eslint-disable-line

  const filtered = useMemo(() => items.filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [it.role, it.name, it.organization, it.phone, it.email].some((v) => (v || "").toLowerCase().includes(s));
  }), [items, q]);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setPanel(true); setDirOpen(true); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it }); setEditId(it.id); setPanel(true); setDirOpen(false); };

  const searchDirectory = async () => {
    setDirLoading(true);
    try {
      const params = {};
      if (dirSearch.trim()) params.q = dirSearch.trim();
      if (dirRoleFilter) params.role = dirRoleFilter;
      const r = await api.get("/provider-directory", { params });
      setDirResults(r.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setDirLoading(false); }
  };
  useEffect(() => { if (panel && dirOpen) searchDirectory(); }, [panel, dirOpen]); // eslint-disable-line
  // Typeahead: re-run the directory search as the user types or changes the role filter.
  // Debounced by 200ms so 500-provider clinics stop scrolling and start finding.
  useEffect(() => {
    if (!panel || !dirOpen) return;
    const t = setTimeout(() => { searchDirectory(); }, 200);
    return () => clearTimeout(t);
  }, [dirSearch, dirRoleFilter, panel, dirOpen]);

  const pickFromDirectory = (p) => {
    setForm({
      role: p.role || "", name: p.name || "", organization: p.organization || "",
      phone: p.phone || "", email: p.email || "", fax: p.fax || "",
      npi: p.npi || "", address: p.address || "",
      is_primary: false, notes: form.notes || "", directory_id: p.id,
    });
    setDirOpen(false);
    toast.success(`${p.name} auto-filled from directory. Save to attach to this client.`);
  };

  const openAddToDirectory = () => {
    setDirNewForm({
      name: dirSearch.trim(), role: dirRoleFilter || "",
      organization: "", specialty: "", phone: "", email: "", fax: "",
      npi: "", address: "", in_network: false, accepting_new_patients: true, notes: "",
    });
    setAddToDirOpen(true);
  };

  const submitAddToDirectory = async (e) => {
    e.preventDefault();
    if (!dirNewForm.name.trim() || !dirNewForm.role) {
      return toast.error("Name and role are required to add a directory entry");
    }
    setDirNewBusy(true);
    try {
      const r = await api.post("/provider-directory", dirNewForm);
      toast.success(`${r.data.name} added to the clinic directory. Attaching to this client.`);
      setAddToDirOpen(false);
      // Auto-attach the new directory entry to the current client
      pickFromDirectory(r.data);
      searchDirectory();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setDirNewBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role || !form.name.trim()) return toast.error("Role and name are required");
    try {
      if (editId) await api.put(`/care-team/${editId}`, form);
      else await api.post(`/clients/${client.id}/care-team`, form);
      toast.success(editId ? "Care team member updated" : "Care team member added");
      setPanel(false); setDirOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/care-team/${del.id}`); toast.success("Removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="care-team-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading font-bold text-[#3E2723]">Care Team</h3>
          <div className="text-[11px] text-[#7B6157]">Every clinician, therapist, and specialist coordinating this client&apos;s care. Pick from the clinic directory or add a new contact.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search care team…"
              data-testid="care-team-search"
              className="rounded-full border border-[#E5DFD3] bg-white pl-8 pr-3 py-1.5 text-xs outline-none w-56 focus:border-[#8D6E63]" />
          </div>
          {editable && (
            <Btn variant="outline" data-testid="care-team-add-btn" onClick={openAdd}>
              <Plus size={13} /> Add Provider
            </Btn>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Empty text="No care team members recorded" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#EFEBE0]">
          <table className="w-full text-sm">
            <thead><tr>
              {["Role", "Name", "Organization", "Phone", "Email", "Primary", "Actions"].map((h) => (
                <th key={h} className="bg-[#FDFCF8] border-b border-[#EFEBE0] text-[#4E342E] font-semibold py-2 px-3 text-left whitespace-nowrap">{h === "Actions" ? "" : h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`care-team-row-${it.id}`} className="hover:bg-[#FDFCF8]">
                  <td className="border-b border-[#EFEBE0] py-2 px-3">
                    <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">{it.role}</span>
                    {it.directory_id && <span className="ml-1 rounded-full bg-[#EDE7F6] px-1.5 py-0.5 text-[9px] font-bold text-[#4527A0]" title="Linked to clinic directory">DIR</span>}
                  </td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 text-[#3E2723] font-medium">{it.name}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 text-[#3E2723]">{it.organization || "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 text-[#3E2723]">{it.phone || "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 text-[#3E2723]">{it.email || "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 text-[#3E2723]">{it.is_primary ? "Yes" : "No"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">
                    <div className="flex justify-end gap-1">
                      {editable && (
                        <button type="button" data-testid={`care-team-edit-${it.id}`} onClick={() => openEdit(it)}
                          className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={12} /></button>
                      )}
                      {editable && (
                        <button type="button" data-testid={`care-team-delete-${it.id}`} onClick={() => setDel(it)}
                          className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => { setPanel(false); setDirOpen(false); }}
        title={editId ? "Edit Care Team Member" : "Add Care Team Member"}
        subtitle={editId ? undefined : "Pick from the clinic directory or fill the fields manually."}
        testId="care-team-slide-panel">
        <form onSubmit={submit} className="space-y-3">
          {!editId && (
            <div className="rounded-xl border border-[#EDE7DC] bg-[#FDFCF8] p-3" data-testid="care-team-directory-picker">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-[#4E342E]">
                  <BookOpen size={13} /> Clinic Provider Directory
                </div>
                <button type="button" onClick={() => setDirOpen((s) => !s)}
                  data-testid="care-team-directory-toggle"
                  className="rounded-full border border-[#E5DFD3] bg-white px-2.5 py-1 text-[10px] font-bold text-[#6D4C41] hover:bg-[#F5F2EB]">
                  {dirOpen ? "Hide" : "Search"}
                </button>
              </div>
              {dirOpen && (
                <>
                  <div className="mb-2 flex gap-2">
                    <div className="relative flex-1">
                      <Search size={12} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7B6157]" />
                      <input value={dirSearch} onChange={(e) => setDirSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchDirectory())}
                        placeholder="Type to search by name, specialty, org, or NPI…"
                        data-testid="care-team-directory-search"
                        className="w-full rounded-full border border-[#E5DFD3] bg-white pl-7 pr-3 py-1.5 text-xs outline-none focus:border-[#8D6E63]" />
                    </div>
                    <select value={dirRoleFilter} onChange={(e) => setDirRoleFilter(e.target.value)}
                      data-testid="care-team-directory-role"
                      className="rounded-full border border-[#E5DFD3] bg-white px-2.5 py-1 text-xs outline-none focus:border-[#8D6E63]">
                      <option value="">All roles</option>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button type="button" onClick={searchDirectory} data-testid="care-team-directory-run"
                      className="rounded-full bg-[#4E342E] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#3E2723]">
                      Search
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-[#EFEBE0] bg-white" data-testid="care-team-directory-results">
                    {dirLoading ? (
                      <div className="p-4 text-center text-xs text-[#7B6157]">Searching…</div>
                    ) : dirResults.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[#7B6157]">
                        <div>No providers match. Try a different filter or fill fields manually below.</div>
                        {isAdmin && (
                          <button type="button" onClick={openAddToDirectory}
                            data-testid="care-team-directory-add-new"
                            className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#4E342E] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#3E2723]">
                            <Plus size={12} /> Add &ldquo;{dirSearch.trim() || "new provider"}&rdquo; to Directory
                          </button>
                        )}
                      </div>
                    ) : dirResults.map((p) => (
                      <button key={p.id} type="button" onClick={() => pickFromDirectory(p)}
                        data-testid={`care-team-directory-pick-${p.id}`}
                        className="w-full border-b border-[#F1EEE7] p-2.5 text-left text-xs hover:bg-[#FDFCF8] last:border-b-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-bold text-[#3E2723]">{p.name}</div>
                            <div className="text-[10px] text-[#7B6157]">{p.role}{p.specialty ? ` - ${p.specialty}` : ""}</div>
                            <div className="text-[10px] text-[#7B6157]">{p.organization || "-"} - {p.phone || "-"}</div>
                          </div>
                          <div className="flex flex-col items-end gap-0.5">
                            {p.in_network && <span className="rounded-full bg-[#E8F5E9] px-1.5 py-0.5 text-[9px] font-bold text-[#2E7D32]">In-network</span>}
                            {p.accepting_new_patients ? <span className="rounded-full bg-[#EDE7DC] px-1.5 py-0.5 text-[9px] font-bold text-[#4E342E]">Accepting</span> : <span className="rounded-full bg-[#FFEBEE] px-1.5 py-0.5 text-[9px] font-bold text-[#C62828]">Not accepting</span>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {form.directory_id && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#EDE7F6] px-2.5 py-1.5 text-[11px] font-bold text-[#4527A0]" data-testid="care-team-directory-attached">
                  Attached to directory entry - future edits sync manually
                  <button type="button" onClick={() => setF("directory_id", "")} className="ml-auto text-[#4527A0] hover:text-[#311B92]">
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Role *</FLabel><FSelect value={form.role} onChange={(e) => setF("role", e.target.value)} data-testid="care-team-field-role">
              <option value="">Select…</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </FSelect></div>
            <div><FLabel>Full Name *</FLabel><FInput value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="care-team-field-name" /></div>
            <div className="col-span-2"><FLabel>Organization</FLabel><FInput value={form.organization} onChange={(e) => setF("organization", e.target.value)} data-testid="care-team-field-organization" /></div>
            <div><FLabel>Phone</FLabel><FInput value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="care-team-field-phone" /></div>
            <div><FLabel>Email</FLabel><FInput value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="care-team-field-email" /></div>
            <div><FLabel>Fax</FLabel><FInput value={form.fax} onChange={(e) => setF("fax", e.target.value)} /></div>
            <div><FLabel>NPI Number</FLabel><FInput value={form.npi} onChange={(e) => setF("npi", e.target.value)} data-testid="care-team-field-npi" /></div>
            <div className="col-span-2"><FLabel>Address</FLabel>
              <textarea rows={2} value={form.address} onChange={(e) => setF("address", e.target.value)}
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!form.is_primary} onChange={(e) => setF("is_primary", e.target.checked)}
                  className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" data-testid="care-team-field-primary" />
                <span className="text-sm text-[#5D4037]">Primary contact for this role</span>
              </label>
            </div>
            <div className="col-span-2"><FLabel>Notes</FLabel>
              <textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)}
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="care-team-save-btn" type="submit">{editId ? "Save" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove}
        title="Remove care team member" description={del ? `Remove ${del.name}?` : ""} />

      <SlidePanel open={addToDirOpen} onClose={() => setAddToDirOpen(false)}
        title="Add New Provider to Directory"
        subtitle="Admin only. Once saved, this provider is available for every client across the clinic."
        testId="care-team-directory-add-panel">
        <form onSubmit={submitAddToDirectory} className="space-y-3">
          <div className="rounded-xl border border-[#EDE7DC] bg-[#FDFCF8] p-3 text-xs text-[#5D4037]">
            Fields marked with an asterisk are required. Everything else can be filled in later by an admin.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Full Name *</FLabel>
              <FInput data-testid="dir-new-name" value={dirNewForm.name}
                onChange={(e) => setDirNewForm((s) => ({ ...s, name: e.target.value }))} /></div>
            <div><FLabel>Role *</FLabel>
              <FSelect data-testid="dir-new-role" value={dirNewForm.role}
                onChange={(e) => setDirNewForm((s) => ({ ...s, role: e.target.value }))}>
                <option value="">Select…</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </FSelect></div>
            <div className="col-span-2"><FLabel>Organization</FLabel>
              <FInput data-testid="dir-new-organization" value={dirNewForm.organization}
                onChange={(e) => setDirNewForm((s) => ({ ...s, organization: e.target.value }))} /></div>
            <div><FLabel>Specialty</FLabel>
              <FInput value={dirNewForm.specialty}
                onChange={(e) => setDirNewForm((s) => ({ ...s, specialty: e.target.value }))} /></div>
            <div><FLabel>NPI Number</FLabel>
              <FInput data-testid="dir-new-npi" value={dirNewForm.npi}
                onChange={(e) => setDirNewForm((s) => ({ ...s, npi: e.target.value }))} /></div>
            <div><FLabel>Phone</FLabel>
              <FInput data-testid="dir-new-phone" value={dirNewForm.phone}
                onChange={(e) => setDirNewForm((s) => ({ ...s, phone: e.target.value }))} /></div>
            <div><FLabel>Email</FLabel>
              <FInput data-testid="dir-new-email" value={dirNewForm.email}
                onChange={(e) => setDirNewForm((s) => ({ ...s, email: e.target.value }))} /></div>
            <div><FLabel>Fax</FLabel>
              <FInput value={dirNewForm.fax}
                onChange={(e) => setDirNewForm((s) => ({ ...s, fax: e.target.value }))} /></div>
            <div className="col-span-2"><FLabel>Address</FLabel>
              <textarea rows={2} value={dirNewForm.address}
                onChange={(e) => setDirNewForm((s) => ({ ...s, address: e.target.value }))}
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div className="col-span-2 flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={dirNewForm.in_network}
                  onChange={(e) => setDirNewForm((s) => ({ ...s, in_network: e.target.checked }))}
                  className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" />
                <span>In-network</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={dirNewForm.accepting_new_patients}
                  onChange={(e) => setDirNewForm((s) => ({ ...s, accepting_new_patients: e.target.checked }))}
                  className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" />
                <span>Accepting new patients</span>
              </label>
            </div>
            <div className="col-span-2"><FLabel>Notes</FLabel>
              <textarea rows={2} value={dirNewForm.notes}
                onChange={(e) => setDirNewForm((s) => ({ ...s, notes: e.target.value }))}
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setAddToDirOpen(false)}>Cancel</Btn>
            <Btn data-testid="dir-new-save" type="submit" disabled={dirNewBusy}>
              {dirNewBusy ? "Saving…" : "Save and Attach"}
            </Btn>
          </div>
        </form>
      </SlidePanel>
    </div>
  );
}
